import { PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errorMessage } from '../api/client';
import { useCreateInvestor, useDeleteInvestor, useInvestors, useUpdateInvestorStatus, type InvestorQuery } from '../api/hooks';
import { MAX_VIDEO_SECONDS } from '../components/investor/documentRules';
import PendingDocumentPicker from '../components/investor/PendingDocumentPicker';
import PasswordConfirmModal from '../components/common/PasswordConfirmModal';
import { useAuth } from '../context/AuthContext';
import type { DocumentType, InvestorListItem } from '../types';
import { apiDate, money } from '../utils/format';
import { autoSendAccountDetails, newAccountContact } from '../utils/quickShare';
import { paymentMethodOptions } from '../utils/paymentMethods';
import { usePasswordConfirm } from '../utils/confirmWithPassword';
import { offerDeactivateOrForceDelete } from '../utils/historyAwareDelete';
import { useTranslation } from 'react-i18next';

const DEAL_TYPE_OPTIONS = [
  { value: 'PercentageBased', labelKey: 'forms.percentageHint' },
  { value: 'FixedReturn', labelKey: 'forms.fixedInvestmentHint' },
];

const CNIC_PATTERN = /^\d{5}-?\d{7}-?\d$/;
const MOBILE_PATTERN = /^03\d{2}-?\d{7}$/;

export default function InvestorsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [query, setQuery] = useState<InvestorQuery>({ page: 1, pageSize: 20, sortBy: 'created_at', sortDir: 'desc' });
  const [modalOpen, setModalOpen] = useState(false);
  const deleteInvestor = useDeleteInvestor();
  const updateStatus = useUpdateInvestorStatus();
  const passwordConfirm = usePasswordConfirm();

  const { data, isFetching, error } = useInvestors(query);

  const onDelete = async (row: InvestorListItem) => {
    if (!(await passwordConfirm.confirm(`delete ${row.investorCode} — ${row.fullName}`))) return;

    Modal.confirm({
      title: `Delete ${row.investorCode} — ${row.fullName}?`,
      content:
        'This only succeeds if the investor has no transaction history. If they have any investments, withdrawals or profit payments, you will be offered the choice to deactivate instead.',
      okText: 'Delete permanently',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteInvestor.mutateAsync({ id: row.id });
          message.success('Investor deleted.');
        } catch (err) {
          if (errorMessage(err).includes('has transaction history')) {
            offerDeactivateOrForceDelete({
              code: row.investorCode,
              fullName: row.fullName,
              onDeactivate: () => updateStatus.mutateAsync({ id: row.id, status: 'Inactive' }),
              onForceDelete: () => deleteInvestor.mutateAsync({ id: row.id, force: true }),
              deactivatedMessage: 'Investor deactivated.',
              deletedMessage: 'Investor and all transaction history deleted.',
            });
          } else {
            Modal.error({ title: 'Could not delete this investor', content: errorMessage(err) });
          }
        }
      },
    });
  };

  return (
    <>
      <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Investors
        </Typography.Title>
        {canWrite && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
            Add investor
          </Button>
        )}
      </Space>

      {error && <Alert type="error" showIcon message={errorMessage(error)} style={{ marginBottom: 16 }} />}

      <Card>
        <Input.Search
          allowClear
          placeholder="Search by name, mobile, CNIC or code"
          style={{ maxWidth: 360, marginBottom: 16 }}
          onSearch={(value) => setQuery((q) => ({ ...q, search: value || undefined, page: 1 }))}
        />

        <Table<InvestorListItem>
          rowKey="id"
          loading={isFetching}
          dataSource={data?.items ?? []}
          scroll={{ x: 'max-content' }}
          onRow={(row) => ({ onClick: () => navigate(`/investors/${row.id}`), style: { cursor: 'pointer' } })}
          pagination={{
            current: query.page,
            pageSize: query.pageSize,
            total: data?.totalCount ?? 0,
            showSizeChanger: true,
            showTotal: (total) => `${total} investors`,
          }}
          onChange={(pagination, _filters, sorter) => {
            const s = sorter as SorterResult<InvestorListItem>;
            setQuery((q) => ({
              ...q,
              page: pagination.current ?? 1,
              pageSize: pagination.pageSize ?? 20,
              sortBy: (s.field as string) ? toColumn(s.field as string) : q.sortBy,
              sortDir: s.order === 'ascend' ? 'asc' : 'desc',
            }));
          }}
          columns={[
            { title: t('table.code'), dataIndex: 'investorCode', sorter: true },
            { title: t('table.name'), dataIndex: 'fullName', sorter: true },
            { title: t('party.mobile'), dataIndex: 'mobile', render: (v: string | null) => v ?? '—' },
            { title: t('party.cnic'), dataIndex: 'cnic', render: (v: string | null) => v ?? '—' },
            {
              title: t('common.status'),
              dataIndex: 'status',
              render: (value: string) => <Tag color={value === 'Active' ? 'green' : 'default'}>{value}</Tag>,
            },
            // The two books are reported side by side and never summed: an investor may hold both,
            // and mixing accrued profit with an agreed return would not mean anything.
            { title: t('finance.percentageInvestment'), dataIndex: 'totalCapital', align: 'right', sorter: true, render: money },
            {
              title: t('finance.percentageProfitRemaining'),
              dataIndex: 'outstandingProfit',
              align: 'right',
              render: (value: number) => (
                <span style={{ color: value > 0 ? '#FF0000' : undefined }}>{money(value)}</span>
              ),
            },
            { title: t('finance.fixedInvestment'), dataIndex: 'fixedCapital', align: 'right', render: money },
            {
              title: t('finance.fixedRemaining'),
              dataIndex: 'fixedRemaining',
              align: 'right',
              render: (value: number) => (
                <span style={{ color: value > 0 ? '#FF0000' : undefined }}>{money(value)}</span>
              ),
            },
            {
              title: t('table.actions'),
              render: (_, row) => (
                <Space onClick={(e) => e.stopPropagation()}>
                  <Button type="link" size="small" onClick={() => navigate(`/investors/${row.id}`)}>
                    View
                  </Button>
                  {canWrite && (
                    <Button danger type="link" size="small" onClick={() => onDelete(row)}>
                      Delete
                    </Button>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <CreateInvestorModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <PasswordConfirmModal state={passwordConfirm.state} />
    </>
  );
}

/** Maps the antd column dataIndex to the SQL column the API sorts on. */
function toColumn(field: string) {
  const map: Record<string, string> = {
    investorCode: 'investor_code',
    fullName: 'full_name',
    totalCapital: 'total_capital',
    createdAt: 'created_at',
  };
  return map[field] ?? 'created_at';
}

function CreateInvestorModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const createInvestor = useCreateInvestor();
  const [photo, setPhoto] = useState<File | null>(null);
  const [cnicImage, setCnicImage] = useState<File | null>(null);
  const [agreementVideo, setAgreementVideo] = useState<File | null>(null);
  const [agreementImage, setAgreementImage] = useState<File | null>(null);
  const [transactionProof, setTransactionProof] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const dealType = Form.useWatch('dealType', form) as string | undefined;
  const paymentMethod = Form.useWatch('paymentMethod', form) as string | undefined;

  const resetAll = () => {
    form.resetFields();
    setPhoto(null);
    setCnicImage(null);
    setAgreementVideo(null);
    setAgreementImage(null);
    setTransactionProof(null);
  };

  /**
   * Documents can only be attached once the investor row exists, so they are uploaded after
   * creation. A failed upload must not read as a failed creation — the account is already real,
   * so problems are reported per-file and the admin can retry from the profile tab.
   */
  const uploadPending = async (investorId: number, initialInvestmentId: number | null): Promise<string[]> => {
    const pending: Array<[DocumentType, File | null]> = [
      ['photo', photo],
      ['cnic-image', cnicImage],
      ['agreement-video', agreementVideo],
      ['agreement-image', agreementImage],
    ];

    const failed: string[] = [];
    for (const [type, file] of pending) {
      if (!file) continue;
      try {
        const body = new FormData();
        body.append('file', file);
        await api.post(`/investors/${investorId}/documents/${type}`, body, {
          headers: { 'Content-Type': undefined },
        });
      } catch {
        failed.push(type);
      }
    }

    // The opening tranche does not exist until the investor has been created, so its proof is
    // attached here with the documents rather than sent in the create request.
    if (transactionProof && initialInvestmentId !== null) {
      try {
        const body = new FormData();
        body.append('file', transactionProof);
        await api.post(
          `/investors/${investorId}/investments/${initialInvestmentId}/transaction-image`,
          body,
          { headers: { 'Content-Type': undefined } },
        );
      } catch {
        failed.push('transaction proof');
      }
    }

    return failed;
  };

  const onFinish = async (values: Record<string, unknown>) => {
    // Documents are optional — the account can be created without any of them and attached later
    // from the profile tab.
    try {
      const result = await createInvestor.mutateAsync({
        ...values,
        initialInvestmentDate: apiDate(values.initialInvestmentDate as dayjs.Dayjs | undefined),
        returnDate: apiDate(values.returnDate as dayjs.Dayjs | undefined),
      });

      setUploading(true);
      const failedUploads = await uploadPending(result.investor.id, result.initialInvestmentId);
      setUploading(false);
      if (failedUploads.length > 0) {
        message.warning(
          `Investor created, but these files did not upload: ${failedUploads.join(', ')}. Add them from the profile tab.`,
        );
      }

      // Fires by itself: the server sends when WhatsApp is configured, otherwise WhatsApp is
      // opened here with the message ready. Either way nothing has to be clicked in this app.
      autoSendAccountDetails(
        newAccountContact({
          partyType: 'Investor',
          name: result.investor.fullName,
          fatherName: result.investor.fatherName,
          phone: result.investor.mobile,
          cnic: result.investor.cnic,
          code: result.investor.investorCode,
          dealType: (values.dealType as 'PercentageBased' | 'FixedReturn') ?? 'PercentageBased',
          initialAmount: Number(values.initialAmount ?? 0),
          returnAmount: values.returnAmount ? Number(values.returnAmount) : null,
          openedOn: apiDate(values.initialInvestmentDate as dayjs.Dayjs | undefined) ?? '',
        }),
        result.whatsAppSent ?? false,
      );

      Modal.success({
        title: 'Investor created',
        width: 480,
        content: (
          <div>
            <p>
              Code / username: <strong>{result.username}</strong>
            </p>
            <p>
              Temporary password: <strong>{result.generatedPassword}</strong>
            </p>
            <p style={{ color: '#FF0000' }}>Save this password now — it will not be shown again.</p>
          </div>
        ),
      });
      resetAll();
      onClose();
    } catch (err) {
      setUploading(false);
      message.error(errorMessage(err));
    }
  };

  return (
    <Modal maskClosable={false}
      title={t('forms.addInvestor')}
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={createInvestor.isPending || uploading}
      okText="Create"
      width={560}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish} preserve={false}>
        <Form.Item name="fullName" label={t('party.fullName')} rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="fatherName" label="Father's name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="mobile"
          label={t('party.mobile')}
          rules={[{ required: true }, { pattern: MOBILE_PATTERN, message: 'Enter an 11-digit mobile number' }]}
        >
          <Input placeholder="03001234567" />
        </Form.Item>
        {/* CNIC can never be edited once the account exists, so it is worth getting right here. */}
        <Form.Item
          name="cnic"
          label={t('party.cnic')}
          rules={[{ required: true }, { pattern: CNIC_PATTERN, message: 'Enter a 13-digit CNIC number' }]}
          help="Cannot be changed after the account is created."
        >
          <Input placeholder="3520212345671" />
        </Form.Item>
        <Form.Item name="address" label={t('party.address')} rules={[{ required: true }]} style={{ marginTop: 16 }}>
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="paymentMethod" label={t('finance.paymentMethod')} rules={[{ required: true }]}>
          <Select placeholder="Select how money moves to and from this investor" options={paymentMethodOptions} />
        </Form.Item>
        {paymentMethod !== 'Cash' && (
          <Form.Item name="bankAccount" label={t('finance.accountNumber')} rules={[{ required: true }]}>
            <Input placeholder="Account title, IBAN or wallet number" />
          </Form.Item>
        )}

        <Divider orientation="left" plain style={{ marginTop: 8 }}>
          Documents
        </Divider>
        <PendingDocumentPicker
          type="photo"
          label={t('forms.profilePhoto')}
          hint="JPG, PNG or WebP · max 5 MB"
          file={photo}
          onChange={setPhoto}
        />
        <PendingDocumentPicker
          type="cnic-image"
          label={t('party.cnicImage')}
          hint="JPG, PNG or WebP · max 5 MB"
          file={cnicImage}
          onChange={setCnicImage}
        />
        <PendingDocumentPicker
          type="agreement-video"
          label={t('party.agreementVideo')}
          hint={`MP4 or WebM · max ${MAX_VIDEO_SECONDS} seconds · 25 MB`}
          file={agreementVideo}
          onChange={setAgreementVideo}
        />
        <PendingDocumentPicker
          type="agreement-image"
          label={t('party.agreementImage')}
          hint="JPG, PNG or WebP · max 5 MB"
          file={agreementImage}
          onChange={setAgreementImage}
        />

        {/* Proof of the opening payment, attached to the investment row the Amount field below
            creates. A fixed-return account books its opening entry in the fixed book, which the
            upload endpoint does not cover, so the picker is only offered where it can be stored. */}
        {dealType !== 'FixedReturn' && (
          <PendingDocumentPicker
            type="transaction-image"
            label={t('finance.transactionProofOptional')}
            hint="Bank slip, JazzCash/EasyPaisa screenshot or cash voucher · JPG, PNG or WebP · max 5 MB"
            file={transactionProof}
            onChange={setTransactionProof}
          />
        )}

        <Divider orientation="left" plain>
          Initial investment
        </Divider>
        <Form.Item
          name="dealType"
          label={t('forms.investmentType')}
          rules={[{ required: true }]}
          initialValue="PercentageBased"
          style={{ marginTop: 8 }}
        >
          <Select options={DEAL_TYPE_OPTIONS.map(({ value, labelKey }) => ({ value, label: t(labelKey) }))} />
        </Form.Item>
        <Form.Item name="initialAmount" label={t('common.amount')} rules={[{ required: true }]}>
          <InputNumber min={0.01} style={{ width: '100%' }} />
        </Form.Item>

        {dealType === 'FixedReturn' ? (
          <>
            <Form.Item
              name="returnAmount"
              label={t('forms.fixedReturnAmount')}
              rules={[
                { required: true },
                ({ getFieldValue }) => ({
                  validator: (_, value: number) =>
                    value === undefined || value === null || value >= (getFieldValue('initialAmount') ?? 0)
                      ? Promise.resolve()
                      : Promise.reject(new Error('The return cannot be less than the amount invested.')),
                }),
              ]}
              help="The whole sum coming back, capital included. No monthly rate applies."
            >
              <InputNumber min={0.01} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="returnDate"
              label={t('forms.returnDueOn')}
              rules={[{ required: true }]}
              help="Becomes the default date for any further investments on this account."
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </>
        ) : (
          /* Optional even when an amount is entered: staff often take the money in before the rate
             is agreed. The tranche is then booked at 0% and accrues nothing until it is set. */
          <Form.Item
            name="initialProfitRate"
            label={t('finance.monthlyProfitRate')}
            help="Optional. Leave blank if the rate is not agreed yet — no profit accrues until it is set."
          >
            <InputNumber min={0.01} max={99.99} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
        )}
        <Form.Item name="initialInvestmentDate" label={t('finance.investmentDate')} rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
        </Form.Item>
        <Form.Item name="notes" label={t('common.notes')}>
          <Input.TextArea rows={2} placeholder="Optional notes about this account" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
